// SHA-256 of the signed-in user's email for Enhanced Conversions (GA4) /
// Advanced Matching (Meta) / TikTok's hashed email. Normalized lowercase +
// trimmed per platform specs; only the digest ever leaves the component —
// raw emails stay out of the dataLayer, consistent with the policy rule that
// forbids user-identifying values in event parameters.

let cachedHash: string | null = null;
let inflight: Promise<void> | null = null;
const seenEmails = new Set<string>();

async function sha256Hex(input: string): Promise<string | null> {
  if (typeof crypto === 'undefined' || !crypto.subtle || !crypto.subtle.digest) {
    return null; // Non-secure context — hashing unavailable, identity simply absent.
  }
  try {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return null;
  }
}

/** Starts (or refreshes) hashing for the given email; fire-and-forget. */
export function setIdentityEmail(email: string | undefined | null): void {
  if (!email) {
    return;
  }
  const normalized = `${email}`.trim().toLowerCase();
  if (!normalized || seenEmails.has(normalized)) {
    return;
  }
  seenEmails.add(normalized);
  inflight = (inflight ?? Promise.resolve())
    .then(() => sha256Hex(normalized))
    .then((hash) => {
      // Last write wins so account switches replace the stale hash.
      cachedHash = hash ?? cachedHash;
    })
    .catch(() => undefined);
}

/** Resolved digest, or null while still hashing / when unavailable. */
export function getIdentityEmailHash(): string | null {
  void inflight;
  return cachedHash;
}

export function resetIdentityForTests(): void {
  cachedHash = null;
  inflight = null;
  seenEmails.clear();
}
